import { Box, Paper, useTheme } from '@mui/material';
import { Modal } from '@mui/material';
import { Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import { useIntl } from 'react-intl';

import { Button, Icon } from '@verantos-dev/shared-components';

interface ConfirmDeleteModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  itemLabel: string;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
  error?: string | null;
}

export const ConfirmDeleteModal = ({
  showModal,
  setShowModal,
  itemLabel,
  onConfirm,
  isLoading,
  error,
}: ConfirmDeleteModalProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  return (
    <Modal
      open={showModal}
      onClose={() => setShowModal(false)}
      aria-labelledby="confirm-delete-modal-title"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          p: theme.spacing(6),
          outline: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography id="confirm-delete-modal-title" variant="h6">
              {formatMessage({ id: 'workspaceCommon.confirmDeleteModal.title' })}
            </Typography>
            <IconButton onClick={() => setShowModal(false)} size="small">
              <Icon id="confirm-delete-modal-close-icon" icon="clear" width={16} />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(4),
              border: `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <Box sx={{ display: 'flex', p: theme.spacing(4) }}>
              <Icon id="confirm-del-icon" icon="delete" sx={{ mr: theme.spacing(3) }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {formatMessage({ id: 'workspaceCommon.confirmDeleteModal.warning.title' })}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.error.main }}>
                  {formatMessage({ id: 'workspaceCommon.confirmDeleteModal.warning' })}
                </Typography>
                <Box sx={{ display: 'flex', mt: theme.spacing(3), gap: theme.spacing(1) }}>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    {formatMessage({ id: 'workspaceCommon.confirmDeleteModal.description.prefix' })}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ color: theme.palette.text.secondary, fontWeight: 'bold' }}
                  >
                    {itemLabel}
                  </Typography>
                </Box>
                {error && (
                  <Typography variant="body2" sx={{ color: theme.palette.error.main }}>
                    {error}
                  </Typography>
                )}
                {isLoading && (
                  <Typography variant="body1">
                    {formatMessage({ id: 'workspaceCommon.confirmDeleteModal.loadingMessage' })}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: theme.spacing(3),
              justifyContent: 'flex-end',
            }}
          >
            <Button
              id="cancel-confirm-delete-button"
              displayLabel={formatMessage({
                id: 'workspaceCommon.confirmDeleteModal.cancelButton',
              })}
              variant="text"
              sx={{ fontSize: '15px', textTransform: 'none' }}
              onClickHandler={() => setShowModal(false)}
              disabled={isLoading}
            />
            <Button
              id="confirm-delete-button"
              displayLabel={formatMessage({
                id: 'workspaceCommon.confirmDeleteModal.confirmButton',
              })}
              variant="contained"
              sx={{
                fontSize: '15px',
                textTransform: 'none',
                backgroundColor: theme.palette.error.dark,
              }}
              onClickHandler={onConfirm}
              disabled={isLoading}
            />
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};
